import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettings } from '@asamco/base';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';

import { GroupRelation } from '../object-permission/group-relation-interface';
import { ObjectPermission } from '../object-permission/object-permission-interface';
import { Permission } from './permission.interface';

/**
 *Service for permissions
 *
 * @export
 */
@Injectable({
	providedIn: 'root',
	deps: [HttpClient, AppSettings],
})
export class PermissionService {
	/**
	 *Creates an instance of PermissionService.
	 * @param http
	 * @param appSettings
	 */
	constructor(private http: HttpClient, private appSettings: AppSettings) {}

	/**
	 *Get user permissions by id
	 *
	 * @param userId
	 * @returns
	 */
	getUserPermissions(userId: number) {
		return this.http.get<Permission>(`permission/tree/user/${userId}`).pipe(shareReplay(1));
	}

	/**
	 *Get user permissions by id
	 *
	 * @param {number} userId
	 * @returns
	 * @memberof PermissionService
	 */
	getUserPermissionsInScope(userId: number, scope: string, objectId: number) {
		return this.http.get<Permission>(`permission/tree/user/${userId}/${scope}/${objectId}`).pipe(shareReplay(1));
	}

	/**
	 *Get group permission by id
	 *
	 * @param groupId
	 * @returns
	 */
	getGroupPermissions(groupId: number): Observable<Permission[]> {
		return this.http.get<Permission[]>(`permission/tree/group/${groupId}`);
	}

	/**
	 *Assign permission to group
	 *
	 * @param groupId
	 * @param permissionKey
	 * @returns
	 */
	assignPermission(groupId: number, permissionKey: string) {
		return this.http
			.post(`permission/assign`, {
				permissionKey: permissionKey,
				groupId: groupId,
			})
			.pipe(shareReplay(1));
	}

	/**
	 *Revoke permission of group
	 *
	 * @param groupId
	 * @param permissionKey
	 * @returns
	 */
	revokePermission(groupId: number, permissionKey: string) {
		return this.http
			.post(`permission/revoke`, {
				permissionKey: permissionKey,
				groupId: groupId,
			})
			.pipe(shareReplay(1));
	}

	/**
	 *Get grouprelations
	 *
	 * @returns
	 */
	getGroupRelations() {
		return this.http.get<GroupRelation[]>(`ObjectPermission/GetGroupRelations`).pipe(shareReplay(1));
	}

	/**
	 *Get object permissions
	 *
	 * @param groupId
	 * @param groupRelationId
	 * @returns
	 */
	getObjectPermissions(groupId: number, groupRelationId: number) {
		return this.http
			.get<ObjectPermission[]>(`ObjectPermission/GetByGroupAndRelation?groupId=${groupId}&groupRelationId=${groupRelationId}`)
			.pipe(shareReplay(1));
	}

	/**
	 *Set object permissions
	 *
	 * @param groupId
	 * @param groupRelationId
	 * @param objectId
	 * @param shouldHavePermission
	 * @returns
	 */
	setObjectPermission(groupId: number, groupRelationId: number, objectId: number, shouldHavePermission: boolean) {
		return this.http
			.get(
				`ObjectPermission/SetPermission?groupId=${groupId}&groupRelationId=${groupRelationId}&objectId=${objectId}&shouldHavePermission=${shouldHavePermission}`
			)
			.pipe(shareReplay(1));
	}
}
